import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, Infinity } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useTranslation } from "@/hooks/useTranslation";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    day: "",
    month: "",
    year: "",
    name: ""
  });
  const [loading, setLoading] = useState(false);
  const [stars, setStars] = useState([]);

  // Generate stars for background
  useEffect(() => {
    const generateStars = () => {
      const newStars = [];
      for (let i = 0; i < 100; i++) {
        newStars.push({
          id: i,
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          delay: `${Math.random() * 3}s`
        });
      }
      setStars(newStars);
    };
    generateStars();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.day || !formData.month || !formData.year) {
      toast.error("Veuillez remplir tous les champs de date");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/calculate`, {
        day: parseInt(formData.day),
        month: parseInt(formData.month),
        year: parseInt(formData.year),
        name: formData.name || null
      });

      // Navigate to results with data
      navigate("/results", { state: { result: response.data } });
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.response?.data?.detail || "Une erreur s'est produite");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      {/* Stars background */}
      <div className="stars-container">
        {stars.map((star) => (
          <div
            key={star.id}
            className="star"
            style={{
              left: star.left,
              top: star.top,
              animationDelay: star.delay
            }}
          />
        ))}
      </div>

      {/* Main content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 flex flex-col items-center justify-center min-h-screen">
        {/* Logo */}
        <div className="mb-8 logo-pulse" data-testid="elua-logo">
          <img
            src="/elua-logo.png"
            alt="ELUA"
            className="w-64 h-64 object-contain"
          />
        </div>

        {/* Tagline */}
        <h1 className="text-white text-2xl sm:text-3xl font-light text-center mb-12 glow-text" style={{ fontFamily: 'Cinzel, serif' }}>
          {t('tagline_main')}
          <br />
          <span className="text-lg sm:text-xl golden-accent">{t('tagline_sub')}</span>
        </h1>

        {/* Navigation Buttons */}
        <div className="flex gap-4 mb-8">
          <Button
            data-testid="individual-reading-button"
            onClick={() => {}} // Already on this page
            className="bg-gradient-to-r from-[#D4AF37] to-[#F4C430] hover:from-[#F4C430] hover:to-[#D4AF37] text-[#0a2e38] font-semibold px-6 py-3"
            style={{ fontFamily: 'Cinzel, serif' }}
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {t('btn_individual_reading')}
          </Button>
          <Button
            data-testid="relationship-button"
            onClick={() => navigate("/relationship")}
            variant="outline"
            className="border-[#F4C430] bg-white/5 text-[#F4C430] hover:bg-white/10 font-semibold px-6 py-3"
            style={{ fontFamily: 'Cinzel, serif' }}
          >
            <Infinity className="w-4 h-4 mr-2" />
            {t('btn_relationship_square')}
          </Button>
        </div>

        {/* Form Card */}
        <Card className="glass-card w-full max-w-md border-2 golden-border card-entrance" data-testid="birth-date-form">
          <CardContent className="p-8">
            <div className="flex items-center justify-center mb-6">
              <Sparkles className="w-6 h-6 golden-accent mr-2" />
              <h2 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Cinzel, serif' }}>
                {t('btn_individual_reading')}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name (optional) */}
              <div>
                <Label htmlFor="name" className="text-white/90 text-base mb-2 block">
                  {t('label_name')}
                </Label>
                <Input
                  id="name"
                  data-testid="name-input"
                  type="text"
                  placeholder="Votre prénom"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F4C430] focus:ring-[#F4C430]"
                />
              </div>

              {/* Birth date */}
              <div>
                <Label className="text-white/90 text-base mb-3 block">
                  {t('label_birthdate')}
                </Label>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Input
                      data-testid="day-input"
                      type="number"
                      placeholder={t('placeholder_day')}
                      min="1"
                      max="31"
                      value={formData.day}
                      onChange={(e) => setFormData({ ...formData, day: e.target.value })}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F4C430] focus:ring-[#F4C430]"
                      required
                    />
                  </div>
                  <div>
                    <Input
                      data-testid="month-input"
                      type="number"
                      placeholder={t('placeholder_month')}
                      min="1"
                      max="12"
                      value={formData.month}
                      onChange={(e) => setFormData({ ...formData, month: e.target.value })}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F4C430] focus:ring-[#F4C430]"
                      required
                    />
                  </div>
                  <div>
                    <Input
                      data-testid="year-input"
                      type="number"
                      placeholder={t('placeholder_year')}
                      min="1900"
                      max="2100"
                      value={formData.year}
                      onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/40 focus:border-[#F4C430] focus:ring-[#F4C430]"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Submit button */}
              <Button
                data-testid="calculate-button"
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#D4AF37] to-[#F4C430] hover:from-[#F4C430] hover:to-[#D4AF37] text-[#0a2e38] font-semibold py-6 text-lg"
                style={{ fontFamily: 'Cinzel, serif' }}
              >
                {loading ? t('btn_calculate_loading') : t('btn_calculate')}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Footer text */}
        <p className="text-white/60 text-sm mt-8 text-center max-w-md">
          {t('footer_text')}
        </p>

        {/* Testimonials Link */}
        <div className="mt-8">
          <Button
            onClick={() => navigate("/testimonials")}
            variant="outline"
            className="border-[#F4C430]/50 bg-white/5 text-white hover:bg-white/10 font-semibold"
          >
            📖 {t('testimonials_title')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Home;
